import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Navbar.css';
import Logo from './images/logo.svg';
import LanguageSwitcher from './LanguageSwitcher';
import { useLenis } from './SmoothScroll';
import { EASE } from './motion';
import { COMPANY } from '../data/site';

const LINKS = ['about', 'services', 'history', 'gallery', 'contact'];

const Navbar = () => {
  const { t } = useTranslation();
  const lenis = useLenis();
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);

  // Po odscrollování ztmavne; při scrollu dolů se schová, při scrollu nahoru vrátí.
  useMotionValueEvent(scrollY, 'change', (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 40);
    setHidden(y > prev && y > 320);
  });

  useEffect(() => {
    if (open) lenis?.stop();
    else lenis?.start();
    document.body.style.overflow = open ? 'hidden' : '';
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, lenis]);

  const close = () => setOpen(false);

  return (
    <>
      <motion.header
        className={`nav ${scrolled || open ? 'nav--solid' : ''}`}
        animate={{ y: hidden && !open ? '-110%' : '0%' }}
        transition={{ duration: 0.45, ease: EASE }}
      >
        <div className="nav__inner container">
          <Link to="/" className="nav__logo" onClick={close} aria-label={`${COMPANY.name}, ${t('nav.home')}`}>
            <img src={Logo} alt="KOVOJOCH" />
          </Link>

          <nav className="nav__links" aria-label="Hlavní navigace">
            <ul>
              {LINKS.map((id) => (
                <li key={id}>
                  <Link to={`/#${id}`} className="nav__link">
                    <span data-text={t(`nav.${id}`)}>{t(`nav.${id}`)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="nav__actions">
            <LanguageSwitcher className="nav__lang" />
            <Link to="/#contact" className="btn btn--accent btn--sm nav__cta">
              {t('nav.cta')}
            </Link>
            <button
              type="button"
              className={`nav__toggle ${open ? 'is-open' : ''}`}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? t('nav.close') : t('nav.open')}
              onClick={() => setOpen((o) => !o)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            className="nav-overlay"
            initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
            animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <ul className="nav-overlay__links">
              {LINKS.map((id, i) => (
                <li key={id} className="mask">
                  <motion.div
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '110%' }}
                    transition={{ duration: 0.7, delay: 0.15 + i * 0.06, ease: EASE }}
                  >
                    <Link to={`/#${id}`} onClick={close}>
                      {t(`nav.${id}`)}
                    </Link>
                  </motion.div>
                </li>
              ))}
            </ul>
            <motion.div
              className="nav-overlay__foot"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.5 } }}
              exit={{ opacity: 0 }}
            >
              <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              <LanguageSwitcher />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
